import { CustomTable, InputTextCustom } from "medere-ui-framework";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Button } from "primereact/button";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { useEffect, useMemo, useState } from "react";
import useResizeListener from "../../../../../hooks/useResizeListener";
import { Bed } from "../../../../../interface/interface";
import LocationMobileActionMenu, { LocationMobileActionItem } from "../../components/LocationMobileActionMenu";
import LocationActionMenu, { LocationActionItem } from "../../components/LocationActionMenu";
import { bedMonitorText } from "../bedConstants";

interface BedTableProps {
    tableId: string;
    beds: Bed[];
    onEdit: (bed: Bed) => void;
    onDelete: (bed: Bed) => void;
}

interface BedTableRow extends Bed {
    monitortext: string;
    statetext: string;
}

interface SelectOption {
    label: string;
    value: string;
}

interface FilterDTOItem {
    fieldName?: string;
    values?: unknown[];
}

const BED_STATE_ALL_VALUE = "__ALL__";
const BED_PAGE_STEP = 50;

function normalizeText(value: unknown) {
    return String(value ?? "").trim().toLowerCase();
}

function applyFilter(rows: BedTableRow[], filter: FilterDTOItem) {
    const field = filter.fieldName;
    const sourceValues = filter.values;
    let raw: unknown;
    if (Array.isArray(sourceValues)) {
        if (sourceValues.length === 1 && Array.isArray(sourceValues[0])) {
            raw = sourceValues[0];
        } else if (sourceValues.length === 1) {
            raw = sourceValues[0];
        } else {
            raw = sourceValues;
        }
    } else {
        raw = sourceValues;
    }
    if (!field) return rows;

    if (Array.isArray(raw)) {
        const values = raw.map(normalizeText).filter(Boolean);
        if (values.length === 0) return rows;
        if (field === "statetext") {
            return rows.filter((row) => values.some((value) => normalizeText((row as any)[field]) === value));
        }
        return rows.filter((row) => values.some((value) => normalizeText((row as any)[field]).includes(value)));
    }

    const expected = normalizeText(raw);
    if (!expected) return rows;

    if (field === "statetext") {
        return rows.filter((row) => normalizeText((row as any)[field]) === expected);
    }

    return rows.filter((row) => normalizeText((row as any)[field]).includes(expected));
}

function sortRows(rows: BedTableRow[], sortMeta: Array<{ fieldName: string; ascOrder: boolean }>) {
    if (!Array.isArray(sortMeta) || sortMeta.length === 0) return rows;
    const sorted = [...rows];

    sorted.sort((a, b) => {
        for (const sort of sortMeta) {
            const leftRaw = (a as any)?.[sort.fieldName];
            const rightRaw = (b as any)?.[sort.fieldName];
            const leftNumber = Number(leftRaw);
            const rightNumber = Number(rightRaw);

            const isNumericCompare =
                Number.isFinite(leftNumber) &&
                Number.isFinite(rightNumber) &&
                String(leftRaw ?? "").trim() !== "" &&
                String(rightRaw ?? "").trim() !== "";

            let compare = 0;
            if (isNumericCompare) {
                compare = leftNumber - rightNumber;
            } else {
                compare = normalizeText(leftRaw).localeCompare(normalizeText(rightRaw));
            }

            if (compare !== 0) return sort.ascOrder ? compare : -compare;
        }
        return 0;
    });

    return sorted;
}

function selectFilterTemplate(options: ColumnFilterElementTemplateOptions, optionsList: SelectOption[]) {
    const selectedValues = Array.isArray(options.value) ? (options.value as string[]) : [];
    const isAllSelected = optionsList.length > 0 && selectedValues.length === optionsList.length;

    return (
        <MultiSelect
            className="w-full location-bed-custom-table__filter-multiselect"
            style={{ height: "2.25rem", marginBottom: "0.28rem" }}
            value={selectedValues}
            options={optionsList}
            optionLabel="label"
            optionValue="value"
            onChange={(e: MultiSelectChangeEvent) => {
                const selected = (e.value ?? []) as string[];
                options.filterCallback(selected);
                options.filterApplyCallback(selected);
            }}
            placeholder="Todas"
            maxSelectedLabels={1}
            selectedItemsLabel={isAllSelected ? "Todas" : "..."}
        />
    );
}

function singleSelectFilterTemplate(options: ColumnFilterElementTemplateOptions, optionsList: SelectOption[]) {
    const selectedValue = typeof options.value === "string" && options.value !== ""
        ? options.value
        : BED_STATE_ALL_VALUE;

    return (
        <Dropdown
            className="w-full location-bed-custom-table__state-filter-dropdown"
            value={selectedValue}
            options={optionsList}
            optionLabel="label"
            optionValue="value"
            onChange={(e: DropdownChangeEvent) => {
                const rawValue = (e.value ?? BED_STATE_ALL_VALUE) as string;
                const value = rawValue === BED_STATE_ALL_VALUE ? null : rawValue;
                options.filterCallback(value);
                options.filterApplyCallback(value);
            }}
            placeholder="Todos"
        />
    );
}

function textFilterTemplate(options: ColumnFilterElementTemplateOptions) {
    const empty = options.value === undefined || options.value === null || options.value === "";
    return (
        <InputTextCustom
            value={options.value ?? ""}
            onChange={(e: any) => {
                options.filterCallback(e.target.value);
                options.filterApplyCallback(e.target.value);
            }}
            icon={empty ? "pi-search" : undefined}
            iconPos="left"
            placeholder=""
        />
    );
}

export default function BedTable({ tableId, beds, onEdit, onDelete }: Readonly<BedTableProps>) {
    const isMobile = useResizeListener(768);

    const bedRows = useMemo<BedTableRow[]>(
        () =>
            (beds ?? []).map((bed) => ({
                ...bed,
                monitortext: bedMonitorText(bed.monitorcheckperioddefault),
                statetext: bed.active ? "Activo" : "Inactivo",
            })),
        [beds]
    );

    const typeOptions = useMemo<SelectOption[]>(
        () =>
            Array.from(
                new Set(
                    bedRows
                        .map((item) => item.bedtypedescription?.trim())
                        .filter((item): item is string => Boolean(item))
                )
            )
                .sort((a, b) => a.localeCompare(b))
                .map((item) => ({ label: item, value: item })),
        [bedRows]
    );

    const sectorOptions = useMemo<SelectOption[]>(
        () =>
            Array.from(
                new Set(
                    bedRows
                        .map((item) => item.sectorname?.trim())
                        .filter((item): item is string => Boolean(item))
                )
            )
                .sort((a, b) => a.localeCompare(b))
                .map((item) => ({ label: item, value: item })),
        [bedRows]
    );

    const stateOptions = useMemo<SelectOption[]>(
        () => [
            { label: "Todos", value: BED_STATE_ALL_VALUE },
            { label: "Activo", value: "Activo" },
            { label: "Inactivo", value: "Inactivo" },
        ],
        []
    );

    const bedSourceVersion = useMemo(
        () =>
            bedRows
                .map((item) =>
                    [
                        item.bed,
                        item.number ?? "",
                        item.sector ?? "",
                        item.room ?? "",
                        item.healthservice ?? "",
                        item.bedtype ?? "",
                        item.monitortype ?? "",
                        item.monitorcheckperioddefault ?? "",
                        item.active ? 1 : 0,
                    ].join(":")
                )
                .join("|"),
        [bedRows]
    );

    const [tablePageSize, setTablePageSize] = useState(BED_PAGE_STEP);
    const [filteredRowsCount, setFilteredRowsCount] = useState(bedRows.length);
    const canLoadMore = filteredRowsCount > tablePageSize;

    useEffect(() => {
        setTablePageSize(BED_PAGE_STEP);
        setFilteredRowsCount(bedRows.length);
    }, [bedSourceVersion]);

    if (isMobile) {
        return (
            <div className="location-mobile-card-list">
                {(beds ?? []).map((item, index) => {
                    const menuItems: LocationMobileActionItem[] = [
                        { label: "Editar", icon: "pi pi-file-edit", action: () => onEdit(item) },
                        { label: "Eliminar", icon: "pi pi-trash", action: () => onDelete(item) },
                    ];

                    return (
                        <article key={`location-bed-${item.bed}-${index}`} className="location-mobile-card">
                            <div className="location-mobile-card-header">
                                <h4 className="location-mobile-card-title">{item.number || "Sin nombre"}</h4>
                                <LocationMobileActionMenu items={menuItems} ariaLabel="Opciones de la cama" />
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Sector</span>
                                <span className="location-mobile-card-value">{item.sectorname || "-"}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Servicio</span>
                                <span className="location-mobile-card-value">{item.servicename || "-"}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Habitación</span>
                                <span className="location-mobile-card-value">{item.roomnumber || "-"}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Tipo</span>
                                <span className="location-mobile-card-value">{item.bedtypedescription || "-"}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Monitoreo</span>
                                <span className="location-mobile-card-value">{bedMonitorText(item.monitorcheckperioddefault)}</span>
                            </div>
                            <div className="location-mobile-card-row">
                                <span className="location-mobile-card-key">Estado</span>
                                <span className="location-mobile-card-value">{item.active ? "Activo" : "Inactivo"}</span>
                            </div>
                        </article>
                    );
                })}
                {(beds ?? []).length === 0 && (
                    <p className="location-mobile-empty">No hay camas cargadas.</p>
                )}
            </div>
        );
    }

    const getFilteredBeds = async (offSet: number, pageSize: number, params?: any) => {
        const filterItems: FilterDTOItem[] = params?.filterDTO?.filters ?? [];
        const sortedByAppliedFilters = filterItems.reduce(
            (acc, current) => applyFilter(acc, current),
            bedRows
        );
        const sortedRows = sortRows(sortedByAppliedFilters, params?.multiSortMeta ?? []);
        setFilteredRowsCount((prev) => (prev === sortedRows.length ? prev : sortedRows.length));
        return sortedRows.slice(offSet, offSet + pageSize);
    };

    return (
        <>
            <CustomTable
                idTable={tableId}
                className="location-bed-custom-table"
                getItems={getFilteredBeds}
                pageSize={tablePageSize}
                params={{ sourceSize: bedRows.length, sourceVersion: bedSourceVersion, tablePageSize }}
                filterRow
                stateless
                clearFiltersClassName="buttonControl location-bed-custom-table__clear-btn"
                hideFiltersButton={false}
                notFoundText="No hay camas cargadas."
                notFlex
            >
            <Column
                field="number"
                header="Nombre"
                className="column location-bed-col-name"
                filter
                filterElement={textFilterTemplate}
                body={(item: BedTableRow) => item.number || "-"}
            />
            <Column
                field="sectorname"
                header="Sector"
                className="column location-bed-col-sector"
                filter
                filterElement={(options) => selectFilterTemplate(options, sectorOptions)}
                body={(item: BedTableRow) => item.sectorname || "-"}
            />
            <Column
                field="roomnumber"
                header="Habitación"
                className="column location-bed-col-room"
                filter
                filterElement={textFilterTemplate}
                body={(item: BedTableRow) => item.roomnumber || "-"}
            />
            <Column
                field="servicename"
                header="Servicio"
                className="column location-bed-col-service"
                filter
                filterElement={textFilterTemplate}
                body={(item: BedTableRow) => item.servicename || "-"}
            />
            <Column
                field="bedtypedescription"
                header="Tipo"
                className="column location-bed-col-type"
                filter
                filterElement={(options) => selectFilterTemplate(options, typeOptions)}
                body={(item: BedTableRow) => {
                    const value = item.bedtypedescription || "-";
                    return (
                        <span className="location-bed-col-type-cell" title={value}>
                            {value}
                        </span>
                    );
                }}
            />
            <Column
                field="monitortext"
                header="Monitoreo"
                className="column location-bed-col-monitoring"
                headerClassName="location-bed-col-monitoring-header"
                filter
                filterElement={textFilterTemplate}
                body={(item: BedTableRow) => (
                    <span className="location-bed-col-monitoring-cell">{item.monitortext || "-"}</span>
                )}
            />
            <Column
                field="statetext"
                header="Estado"
                className="column location-bed-col-state"
                filter
                filterElement={(options) => singleSelectFilterTemplate(options, stateOptions)}
                body={(item: BedTableRow) => item.statetext}
            />
            <Column
                body={(item: BedTableRow) => {
                    const menuItems: LocationActionItem[] = [
                        {
                            label: "Editar",
                            icon: "pi pi-file-edit",
                            action: () => onEdit(item),
                        },
                        {
                            label: "Eliminar",
                            icon: "pi pi-trash",
                            action: () => onDelete(item),
                        },
                    ];

                    return (
                        <div className="flex justify-content-end">
                            <LocationActionMenu items={menuItems} ariaLabel="Opciones de la cama" />
                        </div>
                    );
                }}
            />
            </CustomTable>
            {canLoadMore && (
                <div className="flex justify-content-end mt-2">
                    <Button
                        type="button"
                        className="buttonMain3"
                        label="Cargar más camas"
                        onClick={() => setTablePageSize((prev) => prev + BED_PAGE_STEP)}
                    />
                </div>
            )}
        </>
    );
}
